import React, { useState, useEffect } from "react";
import { Alert } from "react-bootstrap";
import { FiArrowLeft, FiSave } from "react-icons/fi";
import { saveDirectorBankDetails, getDirectorBankDetails, getCastingDirectorsList } from "../../services/adminApi";
import BankSetupHeader from "./components/BankSetup/BankSetupHeader";
import CastingDirectorInfoSection from "./components/BankSetup/CastingDirectorInfoSection";
import BankAccountDetailsSection from "./components/BankSetup/BankAccountDetailsSection";
import SettlementPreferencesSection from "./components/BankSetup/SettlementPreferencesSection";
import BankStatusSection from "./components/BankSetup/BankStatusSection";
import "./PaymentSettlementCustom.css";

const CastingDirectorBankSetup = ({ userId, record, onBack }) => {
  const getSelectedUserId = () =>
    userId || record?.id || record?.userId || record?.castingDirectorId || record?.casting_director_id || record?.user_id || "";

  const [formData, setFormData] = useState({
    casting_director_id: "",
    casting_director_code: "Auto generated",
    account_holder_name: "",
    account_number: "",
    re_account_number: "",
    bank_name: "",
    ifsc_code: "",
    branch_name: "",
    account_type: "Savings",
    upi_id: "",
    micr_code: "",
    settlement_frequency: "Per Project",
    settlement_day_weekly: "Select day",
    settlement_day_monthly: "Select day of month",
    settlement_advance_days: "",
    commission_type: "Fixed",
    commission_value: 10000,
    is_tds_applicable: true,
    tds_value: 10,
    status: "Active",
    remarks: ""
  });

  const [castingDirectors, setCastingDirectors] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadingDirectors, setLoadingDirectors] = useState(false);
  const [successMsg, setSuccessMsg] = useState("");
  const [errorMsg, setErrorMsg] = useState("");

  const isEditMode = Boolean(getSelectedUserId());

  // 🔄 AUTO-FETCH & AUTO-FILL FORM DATA ON EDIT MOUNT
  useEffect(() => {
    let isMounted = true;

    const initForm = async () => {
      setLoadingDirectors(true);
      try {
        const listRes = await getCastingDirectorsList();
        const cdList = listRes.data?.success ? listRes.data.data || [] : [];
        
        if (isMounted) setCastingDirectors(cdList);

        const selectedUserId = getSelectedUserId();
        if (!selectedUserId) {
          setLoadingDirectors(false);
          return;
        }

        const selectedIdStr = String(selectedUserId);
        const matchedDirector = cdList.find((cd) => String(cd.id) === selectedIdStr);

        let bankData = null;
        try {
          const bankRes = await getDirectorBankDetails(selectedUserId);
          if (bankRes.data?.success && bankRes.data?.data) {
            bankData = bankRes.data.data;
          }
        } catch (err) {
          console.warn("Bank details fetch error / record not found:", err);
        }

        if (!isMounted) return;

        setFormData({
          casting_director_id: selectedIdStr,
          casting_director_code: matchedDirector?.jogetUserId || record?.jogetUserId || record?.joget_user_id || "Auto generated",
          account_holder_name: bankData?.account_holder_name || record?.accountHolderName || record?.account_holder_name || record?.name || matchedDirector?.name || "",
          account_number: bankData?.account_number || record?.accountNumber || record?.account_number || matchedDirector?.accountNumber || "",
          re_account_number: bankData?.account_number || record?.accountNumber || record?.account_number || matchedDirector?.accountNumber || "",
          bank_name: bankData?.bank_name || record?.bankName || record?.bank_name || matchedDirector?.bankName || "",
          ifsc_code: bankData?.ifsc_code || record?.ifscCode || record?.ifsc_code || matchedDirector?.ifscCode || "",
          branch_name: bankData?.branch_name || record?.branchName || record?.branch_name || "",
          account_type: bankData?.account_type || record?.accountType || record?.account_type || matchedDirector?.accountType || "Savings",
          upi_id: bankData?.upi_id || record?.upiId || record?.upi_id || "",
          micr_code: bankData?.micr_code || record?.micrCode || record?.micr_code || "",
          settlement_frequency: bankData?.settlement_frequency || record?.frequency || record?.settlementFrequency || record?.settlement_frequency || matchedDirector?.frequency || "Per Project",
          settlement_day_weekly: bankData?.settlement_day_weekly || record?.settlementDayWeekly || record?.settlement_day_weekly || "Select day",
          settlement_day_monthly: bankData?.settlement_day_monthly || record?.settlementDayMonthly || record?.settlement_day_monthly || "Select day of month",
          settlement_advance_days: bankData?.settlement_advance_days ?? record?.settlementAdvanceDays ?? record?.settlement_advance_days ?? "",
          commission_type: bankData?.commission_type || record?.commission_type || record?.commissionType || "Fixed",
          commission_value: bankData?.commission_value ?? record?.commission_value ?? record?.commissionValue ?? 10000,
          is_tds_applicable: bankData ? (bankData.is_tds_applicable === 1 || bankData.is_tds_applicable === true) : (record?.is_tds_applicable ?? true),
          tds_value: bankData?.tds_value ?? record?.tds_value ?? 10,
          status: bankData?.status || record?.bankStatus || record?.status || matchedDirector?.bankStatus || "Active",
          remarks: bankData?.remarks || record?.remarks || ""
        });

      } catch (error) {
        console.error("Error initializing form:", error);
        if (isMounted) setErrorMsg("Failed to load form data.");
      } finally {
        if (isMounted) setLoadingDirectors(false);
      }
    };

    initForm();

    return () => { isMounted = false; };
  }, [userId, record]);

  // Handlers
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleCheckboxChange = (e) => {
    const { name, checked } = e.target;
    setFormData((prev) => ({ ...prev, [name]: checked }));
  };

  const handleCastingDirectorChange = async (e) => {
    const selectedId = e.target.value;
    const selectedDirector = castingDirectors.find((cd) => String(cd.id) === String(selectedId));

    setFormData((prev) => ({
      ...prev,
      casting_director_id: selectedId,
      casting_director_code: selectedDirector?.jogetUserId || "Auto generated",
      account_holder_name: selectedDirector?.name || "",
      account_number: selectedDirector?.accountNumber || "",
      re_account_number: selectedDirector?.accountNumber || "",
      bank_name: selectedDirector?.bankName || "",
      ifsc_code: selectedDirector?.ifscCode || "",
      settlement_frequency: selectedDirector?.frequency || "Per Project",
      status: selectedDirector?.bankStatus || "Active"
    }));

    if (selectedId) {
      try {
        const res = await getDirectorBankDetails(selectedId);
        if (res.data?.success && res.data?.data) {
          const bank = res.data.data;
          setFormData((prev) => ({
            ...prev,
            casting_director_id: String(selectedId),
            account_holder_name: bank.account_holder_name || selectedDirector?.name || "",
            account_number: bank.account_number || selectedDirector?.accountNumber || "",
            re_account_number: bank.account_number || selectedDirector?.accountNumber || "",
            bank_name: bank.bank_name || selectedDirector?.bankName || "",
            ifsc_code: bank.ifsc_code || selectedDirector?.ifscCode || "",
            branch_name: bank.branch_name || "",
            account_type: bank.account_type || "Savings",
            upi_id: bank.upi_id || "",
            micr_code: bank.micr_code || "",
            settlement_frequency: bank.settlement_frequency || "Per Project",
            settlement_day_weekly: bank.settlement_day_weekly || "Select day",
            settlement_day_monthly: bank.settlement_day_monthly || "Select day of month",
            settlement_advance_days: bank.settlement_advance_days ?? "",
            commission_type: bank.commission_type || "Fixed",
            commission_value: bank.commission_value ?? 10000,
            is_tds_applicable: bank.is_tds_applicable === 1 || bank.is_tds_applicable === true,
            tds_value: bank.tds_value ?? 10,
            status: bank.status || "Active",
            remarks: bank.remarks || ""
          }));
        }
      } catch (err) {
        console.error("Failed to fetch bank details for manually selected CD", err);
      }
    }
  };

  const handleSubmit = async (e) => {
    if (e) e.preventDefault();
    setSuccessMsg("");
    setErrorMsg("");

    if (!formData.casting_director_id) return setErrorMsg("Please select a Casting Director.");
    if (!formData.account_number) return setErrorMsg("Account number is required.");
    if (formData.account_number !== formData.re_account_number) return setErrorMsg("Account numbers do not match!");
    if (!formData.bank_name) return setErrorMsg("Bank name is required.");
    if (!formData.ifsc_code) return setErrorMsg("IFSC code is required.");

    try {
      setLoading(true);
      const payload = {
        userId: formData.casting_director_id,
        account_holder_name: formData.account_holder_name,
        account_number: formData.account_number,
        bank_name: formData.bank_name,
        ifsc_code: formData.ifsc_code,
        branch_name: formData.branch_name,
        account_type: formData.account_type,
        upi_id: formData.upi_id,
        micr_code: formData.micr_code,
        settlement_frequency: formData.settlement_frequency,
        settlement_day_weekly: formData.settlement_day_weekly,
        settlement_day_monthly: formData.settlement_day_monthly,
        settlement_advance_days: formData.settlement_advance_days,
        commission_type: formData.commission_type,
        commission_value: formData.commission_value,
        is_tds_applicable: formData.is_tds_applicable,
        tds_value: formData.is_tds_applicable ? formData.tds_value : 0,
        status: formData.status,
        remarks: formData.remarks
      };

      const res = await saveDirectorBankDetails(payload);
      if (res.data?.success) {
        setSuccessMsg(
          isEditMode
            ? "Payment Master updated successfully! Redirecting in 3 seconds..."
            : "Payment Master saved successfully! Redirecting in 3 seconds..."
        );

        // ⏰ 3 SECONDS (3000ms) AUTO REDIRECT TO LIST PAGE
        setTimeout(() => {
          if (onBack) onBack();
        }, 3000);

      } else {
        setErrorMsg(res.data?.message || "Failed to save Payment Master.");
      }
    } catch (error) {
      setErrorMsg(error.response?.data?.message || "Failed to save Payment Master.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="bma-settlement-page">
      <BankSetupHeader onBack={onBack} handleSubmit={handleSubmit} loading={loading} />

      {successMsg && <Alert variant="success" dismissible onClose={() => setSuccessMsg("")}>{successMsg}</Alert>}
      {errorMsg && <Alert variant="danger" dismissible onClose={() => setErrorMsg("")}>{errorMsg}</Alert>}

      <form onSubmit={handleSubmit}>
        <div className="d-flex flex-column gap-4">
          <CastingDirectorInfoSection
            formData={formData}
            castingDirectors={castingDirectors}
            loadingDirectors={loadingDirectors}
            handleCastingDirectorChange={handleCastingDirectorChange}
          />

          <BankAccountDetailsSection
            formData={formData}
            handleChange={handleChange}
          />

          <SettlementPreferencesSection
            formData={formData}
            handleChange={handleChange}
            handleCheckboxChange={handleCheckboxChange}
          />

          <BankStatusSection
            formData={formData}
            setFormData={setFormData}
            handleChange={handleChange}
          />

          {/* 📌 FORM KE NICHE SAVE & BACK BUTTON */}
          <div className="d-flex justify-content-end align-items-center gap-3 mt-3 pt-3 border-top" style={{ borderColor: "#24283b" }}>
            {onBack && (
              <button type="button" className="bma-btn-outline" onClick={onBack}>
                <FiArrowLeft /> Back
              </button>
            )}
            <button type="submit" className="bma-btn-red px-4" disabled={loading}>
              <FiSave /> {loading ? "Saving..." : "Save Payment Master"}
            </button>
          </div>
        </div>
      </form>
    </div>
  );
};

export default CastingDirectorBankSetup;